/**
 * ╔══════════════════════════════════════════════════════════════════╗
 * ║  ZOJZ.FR — App.js  ·  v10  ·  Scroll Edition                    ║
 * ╠══════════════════════════════════════════════════════════════════╣
 * ║  HomeSection : 5 sections pleine page (scroll vertical)          ║
 * ║  Map / Pricing / News : INCHANGÉS (copie exacte v9)              ║
 * ╚══════════════════════════════════════════════════════════════════╝
 */

import React, { useState, useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence, useInView } from "framer-motion";
import { MapContainer, TileLayer, useMap, Rectangle, Tooltip } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import "leaflet.heat";
import { WifiOff, ArrowDown, ArrowUpRight } from "lucide-react";


// ─────────────────────────────────────────────────────────────────
// DESIGN TOKENS
// ─────────────────────────────────────────────────────────────────
const T = {
  bg:     "#000000",
  border: "#2a2a2a",
  s1:     "rgba(255,255,255,0.025)",
  text:   "#c8d4e0",
  muted:  "#3a4a5a",
  accent: "#00d4ff",   // cyan charte
  warm:   "#f97316",   // orange J
  green:  "#34d399",
  yellow: "#e8e000",   // footer Atmo
};

const A = {
  landscape: "/assets/landscape.jpg",    // Section 1 Hero  (droite)
  techWind:  "/assets/tech-detail.jpg",  // Section 2 Carte (gauche)
  solar:     "/assets/solar-panel.jpg",  // Section 3 Exploit (droite)
  lightning: "/assets/nuage_caca.jpg",    // Section 4 News  (gauche)
  pylons: "/assets/pylons.png"
};

// ─────────────────────────────────────────────────────────────────
// API — INCHANGÉ
// ─────────────────────────────────────────────────────────────────
async function fetchForecast(datetime) {
  const ctrl = new AbortController();
  const t = setTimeout(() => ctrl.abort(), 8000);
  try {
    const res = await fetch(`https://zojz.fr/api/forecast/predictions`, { signal: ctrl.signal });
    if (!res.ok) throw new Error(`API ${res.status}`);
    return await res.json();
  } finally { clearTimeout(t); }
}
const HOURS = [0, 6, 12, 18];
function iso(h) { 
  const d = new Date(); 
  // On règle l'heure locale (Paris) et non UTC
  d.setHours(h, 0, 0, 0); 
  return d.toISOString(); 
}
function computeStats(pts) {
  if (!pts.length) return { tMax: null, tMin: null };
  const v = pts.map(p => p.value - 273.15);
  return { tMax: Math.max(...v).toFixed(1), tMin: Math.min(...v).toFixed(1) };
}

// ─────────────────────────────────────────────────────────────────
// HOOKS — INCHANGÉS
// ─────────────────────────────────────────────────────────────────
function useJitter(base, delta = 0.3, ms = 1900) {
  const [v, setV] = useState(base);
  useEffect(() => {
    const t = setInterval(() => setV(+(base + (Math.random() - 0.5) * 2 * delta).toFixed(2)), ms);
    return () => clearInterval(t);
  }, [base, delta, ms]);
  return v;
}
function useClock() {
  const [ts, setTs] = useState("");
  useEffect(() => {
    const tick = () => setTs(new Date().toLocaleTimeString('fr-FR') + " (Paris)");
    tick(); const t = setInterval(tick, 1000); return () => clearInterval(t);
  }, []);
  return ts;
}
function useApiHealth() {
  const [ok, setOk] = useState(true);
  useEffect(() => {
    async function check() {
      try { 
        // On pointe vers la même URL que la carte
        const r = await fetch('https://zojz.fr/api/forecast/predictions'); 
        setOk(r.ok); 
      } catch { setOk(false); }
    }
    check(); 
    const t = setInterval(check, 30000); 
    return () => clearInterval(t);
  }, []);
  return ok;
}

// ─────────────────────────────────────────────────────────────────
// GLOBAL CSS
// ─────────────────────────────────────────────────────────────────
const CSS = `
@import url('https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@400;600;700&display=swap');

*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
html { scroll-behavior: smooth; }
body {
  background: #000;
  color: #c8d4e0;
  font-family: 'JetBrains Mono', monospace;
  overflow-x: hidden;
}
::-webkit-scrollbar { width: 3px; }
::-webkit-scrollbar-track { background: #000; }
::-webkit-scrollbar-thumb { background: #1a1a1a; }

/* Nav */
.nav-btn { background: none; border: none; cursor: pointer; font-family: inherit; }
.nav-btn:hover { color: #00d4ff !important; }

/* Scanlines */
.scanlines {
  position: absolute; inset: 0; pointer-events: none;
  background: repeating-linear-gradient(0deg, transparent, transparent 3px, rgba(0,0,0,0.06) 3px, rgba(0,0,0,0.06) 4px);
}

/* Leaflet */
.leaflet-container { font-family: 'JetBrains Mono', monospace; }
.leaflet-control-zoom, .leaflet-control-attribution { display: none !important; }

/* Sections scroll */
.home-section {
  min-height: 100vh;
  display: flex;
  position: relative;
  overflow: hidden;
}

/* Animations */
@keyframes blink { 0%,100%{opacity:1} 50%{opacity:0.3} }
.blink { animation: blink 2s ease-in-out infinite; }

@keyframes fadeUp { from { opacity:0; transform:translateY(32px); } to { opacity:1; transform:translateY(0); } }

/* Footer Atmo */
.footer-atmo a {
  color: inherit;
  text-decoration: underline;
  text-underline-offset: 3px;
  font-size: 14px;
}
.footer-atmo a:hover { opacity: 0.7; }

/* CTA Button */
.cta-btn {
  display: inline-flex; align-items: center; gap: 10px;
  padding: 14px 28px;
  background: transparent;
  border: 1px solid #00d4ff;
  color: #00d4ff;
  font-family: inherit; font-size: 13px; font-weight: 700;
  letter-spacing: 1.5px; text-transform: uppercase;
  cursor: pointer;
  transition: background 0.25s, color 0.25s;
}
.cta-btn:hover { background: #00d4ff; color: #000; }
.cta-btn-solid {
  background: #00d4ff; color: #000;
  border: 1px solid #00d4ff;
}
.cta-btn-solid:hover { background: transparent; color: #00d4ff; }
`;

// ─────────────────────────────────────────────────────────────────
// PRIMITIVES
// ─────────────────────────────────────────────────────────────────
function Tag({ children }) {
  return (
    <span style={{
      display: "inline-block",
      fontSize: 9, fontWeight: 700, letterSpacing: "2.5px",
      color: T.accent, border: `1px solid rgba(0,212,255,0.35)`,
      padding: "4px 10px", marginBottom: 20,
    }}>
      {children}
    </span>
  );
}

function SectionTitle({ children, size = "clamp(36px,4.5vw,68px)" }) {
  return (
    <h2 style={{
      fontSize: size, fontWeight: 700, color: "#fff",
      lineHeight: 1.0, letterSpacing: "-2px",
      fontFamily: "'JetBrains Mono', monospace",
    }}>
      {children}
    </h2>
  );
}

function Body({ children, style = {} }) {
  return (
    <p style={{
      fontSize: 15, color: T.muted, lineHeight: 1.85,
      maxWidth: 480, fontWeight: 400,
      ...style,
    }}>
      {children}
    </p>
  );
}

/* Reveal animation wrapper */
function Reveal({ children, delay = 0 }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 36 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.65, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.div>
  );
}

/* Full-bleed image panel */
function ImgPanel({ src, position = "center center", brightness = 0.75, children }) {
  return (
    <div style={{ position: "relative", overflow: "hidden", width: "100%", height: "100%" }}>
      <img src={src} alt="" style={{
        width: "100%", height: "100%",
        objectFit: "cover", objectPosition: position,
        filter: `brightness(${brightness}) saturate(0.9)`,
        display: "block",
      }} onError={(e) => { e.currentTarget.style.display = "none"; }} />
      <div className="scanlines" />
      {/* vignette */}
      <div style={{
        position: "absolute", inset: 0, pointerEvents: "none",
        background: "linear-gradient(to right, rgba(0,0,0,0.4) 0%, transparent 50%)",
      }} />
      {children}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// NAVBAR — Avec Espace Client PRO et Statut de Session
// ─────────────────────────────────────────────────────────────────
function Navbar({ active, setActive, apiOnline, user, setUser, setShowAuth }) {
  const clock = useClock();
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", fn);
    return () => window.removeEventListener("scroll", fn);
  }, []);

  // Fonction pour se déconnecter proprement
  const handleLogout = (e) => {
    e.stopPropagation(); // Évite de déclencher le bouton derrière
    localStorage.removeItem("zojz_user");
    setUser(null);
  };

  return (
    <div style={{
      position: "fixed", top: 0, left: 0, right: 0, height: 44, zIndex: 1000,
      background: scrolled ? "rgba(0,0,0,0.96)" : "rgba(0,0,0,0.88)",
      backdropFilter: "blur(16px)",
      borderBottom: `1px solid ${T.border}`,
      display: "flex", alignItems: "center",
      transition: "background 0.3s",
    }}>
      {/* Logo slot */}
      <div style={{ width: 44, height: "100%", borderRight: `1px solid ${T.border}`,
        display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
        <div style={{ width: 24, height: 24, border: "1px dashed #333", borderRadius: 3 }} />
      </div>

      {["Home", "Map", "Pricing", "News"].map(s => (
        <button key={s} className="nav-btn" onClick={() => setActive(s)} style={{
          height: "100%", padding: "0 22px",
          borderRight: `1px solid ${T.border}`,
          color: active === s ? T.accent : T.muted,
          fontSize: 11, fontWeight: 700, letterSpacing: "0.8px",
          background: active === s ? "rgba(0,212,255,0.05)" : "transparent",
          transition: "color 0.2s, background 0.2s",
          cursor: "pointer",
          borderTop: "none", borderBottom: "none", borderLeft: "none"
        }}>{s.toUpperCase()}</button>
      ))}

      <div style={{ marginLeft: "auto", display: "flex", height: "100%", alignItems: "center" }}>
        
        {/* SECTION ESPACE CLIENT PRO */}
        <div style={{ height: "100%", display: "flex", alignItems: "center", borderLeft: `1px solid ${T.border}` }}>
          {!user ? (
            <button 
              onClick={() => setShowAuth(true)}
              style={{
                height: "100%", padding: "0 20px",
                background: "transparent",
                color: "#fff",
                fontSize: 10, fontWeight: 800, letterSpacing: "1px",
                cursor: "pointer", border: "none",
                transition: "all 0.2s",
              }}
              onMouseEnter={(e) => e.target.style.color = T.accent}
              onMouseLeave={(e) => e.target.style.color = "#fff"}
            >
              ESPACE PRO
            </button>
          ) : (
            <div style={{ display: "flex", height: "100%" }}>
                <div style={{ 
                  padding: "0 15px", display: "flex", alignItems: "center", 
                  fontSize: 10, fontWeight: 800, color: T.accent, 
                  background: "rgba(0,212,255,0.05)", letterSpacing: "0.5px"
                }}>
                  {user?.email ? user.email.split('@')[0].toUpperCase() : "UTILISATEUR"} ({user?.plan_type?.toUpperCase() || "FREE"})
                </div>
                <button 
                  onClick={handleLogout}
                  style={{
                    height: "100%", padding: "0 12px", background: "transparent",
                    border: "none", borderLeft: `1px solid ${T.border}`,
                    color: "#666", fontSize: 9, fontWeight: 700, cursor: "pointer"
                  }}
                  onMouseEnter={(e) => e.target.style.color = "#fff"}
                  onMouseLeave={(e) => e.target.style.color = "#666"}
                >
                  LOGOUT
                </button>
            </div>
          )}
        </div>

        {/* STATUS LIVE / OFFLINE */}
        <div style={{
          padding: "0 18px", height: "100%",
          borderLeft: `1px solid ${T.border}`,
          display: "flex", alignItems: "center", gap: 8,
          fontSize: 10, fontWeight: 700,
          color: apiOnline ? T.green : "#ef4444",
        }}>
          <div style={{
            width: 5, height: 5, borderRadius: "50%",
            background: apiOnline ? T.green : "#ef4444",
            boxShadow: `0 0 8px ${apiOnline ? T.green : "#ef4444"}`,
          }} className={apiOnline ? "blink" : ""} />
          {apiOnline ? "LIVE" : "OFFLINE"}
        </div>

        {/* CLOCK */}
        <div style={{ padding: "0 20px", height: "100%", borderLeft: `1px solid ${T.border}`,
          display: "flex", alignItems: "center" }}>
          <span style={{ fontSize: 10, color: T.muted, fontVariantNumeric: "tabular-nums" }}>{clock}</span>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// HOME SECTION — 5 sections pleine page
// ─────────────────────────────────────────────────────────────────
const THREE_P = ["Prévision.", "Précision.", "Planification."];

function HomeSection({ setActive }) {
  const [pIdx, setPIdx] = useState(0);
  const [pVis, setPVis] = useState(true);
  const accuracy = useJitter(99.26, 0.08, 2200);

  // 3P rotation
  useEffect(() => {
    const t = setInterval(() => {
      setPVis(false);
      setTimeout(() => { setPIdx(i => (i + 1) % 3); setPVis(true); }, 400);
    }, 2500);
    return () => clearInterval(t);
  }, []);

  return (
    <div>
      {/* ════════════════════════════════════════════════════════════
          SECTION 1 — HERO
      ════════════════════════════════════════════════════════════ */}
      <section className="home-section" style={{ alignItems: "center" }}>
        {/* Left — Text */}
        <div style={{
          flex: "0 0 50%", padding: "100px 80px 80px",
          display: "flex", flexDirection: "column",
          justifyContent: "center", gap: 0,
          position: "relative", zIndex: 2,
        }}>
          <Reveal>
            <Tag>METEOROLOGY TRANSFORMED BY AI</Tag>
          </Reveal>

          <Reveal delay={0.08}>
            <div style={{ marginBottom: 6 }}>
              {/* ZOJZ logotype */}
              <div style={{ fontSize: 15, fontWeight: 700, letterSpacing: "4px",
                color: "#fff", marginBottom: 20 }}>
                Z·O·<span style={{ color: T.warm }}>J</span>·Z
              </div>
              <SectionTitle>
                La météo<br />de demain.
              </SectionTitle>
            </div>
          </Reveal>

          {/* 3P word */}
          <Reveal delay={0.14}>
            <div style={{ height: 52, display: "flex", alignItems: "center", marginTop: 12, marginBottom: 28 }}>
              <span style={{
                fontSize: "clamp(20px,2.2vw,32px)", fontWeight: 700,
                color: T.accent,
                opacity: pVis ? 1 : 0,
                filter: pVis ? "blur(0)" : "blur(6px)",
                transition: "opacity 0.4s ease, filter 0.4s ease",
              }}>
                {THREE_P[pIdx]}
              </span>
            </div>
          </Reveal>

          <Reveal delay={0.20}>
            <Body>
              Propulsé par GraphCast &amp; Copernicus, ZOJZ traite les dynamiques
              multicouches de la troposphère pour une analyse locale de haute précision.
              Monitoring en continu des flux de vent et de l'irradiance solaire sur l'ensemble du territoire.
            </Body>
          </Reveal>

          <Reveal delay={0.26}>
            <div style={{ display: "flex", alignItems: "center", gap: 20, marginTop: 44 }}>
              <button className="cta-btn cta-btn-solid"
                onClick={() => setActive("Map")}>
                EXPLORER LA CARTE
                <ArrowUpRight size={14} />
              </button>
            </div>
          </Reveal>

          {/* bottom info strip */}
          <div style={{
            position: "absolute", bottom: 32, left: 80,
            display: "flex", gap: 32,
          }}>
            {[
              { l: "GRAPHCAST v2", c: T.muted },
              { l: "COPERNICUS", c: T.muted },
              { l: "26/02/2026", c: T.accent },
            ].map(({ l, c }) => (
              <span key={l} style={{ fontSize: 9, color: c, letterSpacing: "1.5px" }}>{l}</span>
            ))}
          </div>
        </div>

        {/* Right — Image */}
        <div style={{ flex: "0 0 50%", height: "100vh", position: "sticky", top: 0 }}>
          <ImgPanel src={A.landscape} position="center 60%" brightness={0.78}>
            {/* corner accent */}
            <div style={{ position: "absolute", top: 20, right: 20,
              fontSize: 9, color: "rgba(0,212,255,0.5)", letterSpacing: "1px" }}>
              FRANCE · 46.6°N 2.2°E
            </div>
            {/* corner frame */}
            {[
              { top:0, left:0, width:20, height:1 }, { top:0, left:0, width:1, height:20 },
              { bottom:0, right:0, width:20, height:1 }, { bottom:0, right:0, width:1, height:20 },
            ].map((s, i) => (
              <div key={i} style={{ position:"absolute", background:"rgba(0,212,255,0.45)", ...s }} />
            ))}
          </ImgPanel>
        </div>

        {/* Scroll hint */}
        <div style={{
          position: "absolute", bottom: 32, left: "50%", transform: "translateX(-50%)",
          display: "flex", flexDirection: "column", alignItems: "center", gap: 8,
          fontSize: 9, color: T.muted, letterSpacing: "2px",
          animation: "fadeUp 1s ease 1.2s both",
        }}>
          <span>DÉFILER</span>
          <ArrowDown size={14} color={T.muted} />
        </div>
      </section>

      {/* ════════════════════════════════════════════════════════════
          SECTION 2 — LA CARTE
          Image gauche · Texte droite
      ════════════════════════════════════════════════════════════ */}
      <section className="home-section" style={{ alignItems: "stretch" }}>
        {/* Left image */}
        <div style={{ flex: "0 0 50%", minHeight: "100vh" }}>
          <ImgPanel src={A.techWind} position="center center" brightness={0.7}>
            <div style={{
              position: "absolute", bottom: 24, left: 24,
              fontSize: 9, color: "rgba(0,212,255,0.6)", letterSpacing: "1px",
            }}>
              ÉOLIEN · FRANCE
            </div>
          </ImgPanel>
        </div>

        {/* Right text */}
        <div style={{
          flex: "0 0 50%", padding: "120px 80px",
          display: "flex", flexDirection: "column",
          justifyContent: "center", gap: 28,
          borderLeft: `1px solid ${T.border}`,
        }}>
          <Reveal>
            <Tag>DONNÉES EN TEMPS RÉEL</Tag>
          </Reveal>
          <Reveal delay={0.06}>
            <SectionTitle>
              Organisez vous<br />face à la météo<br />comme jamais.
            </SectionTitle>
          </Reveal>
          <Reveal delay={0.12}>
            <Body>
              Chaque décision énergétique peut maintenant s'appuyer sur des données
              atmosphériques à <strong style={{ color: T.text }}>1 km² de résolution</strong>,
              actualisées toutes les 6 heures. GraphCast prédit le vent, le rayonnement
              et la température avec une fiabilité inégalée pour son prix.
            </Body>
          </Reveal>
          <Reveal delay={0.16}>
            {/* stats mini-grid */}
            <div style={{
              display: "grid", gridTemplateColumns: "1fr 1fr",
              gap: 1, background: T.border,
              border: `1px solid ${T.border}`,
              marginTop: 4, marginBottom: 8,
            }}>
              {[
                { l: "RÉSOLUTION",   v: "1 km²"      },
                { l: "FORECAST",     v: "J + 10"      },
                { l: "MAJ",          v: "Toutes 6h"   },
                { l: "COUVERTURE",   v: "France ent." },
              ].map(({ l, v }) => (
                <div key={l} style={{ background: "#000", padding: "16px 18px" }}>
                  <div style={{ fontSize: 8, color: T.muted, letterSpacing: "1.5px", marginBottom: 4 }}>{l}</div>
                  <div style={{ fontSize: 16, fontWeight: 700, color: "#fff" }}>{v}</div>
                </div>
              ))}
            </div>
          </Reveal>
          <Reveal delay={0.22}>
            <button className="cta-btn" onClick={() => setActive("Map")}>
              EXPLORER LA CARTE
              <ArrowUpRight size={14} />
            </button>
          </Reveal>
        </div>
      </section>

      {/* ════════════════════════════════════════════════════════════
          SECTION 3 — EXPLOITATION & ABONNEMENTS
          Texte gauche · Image droite
      ════════════════════════════════════════════════════════════ */}
      <section className="home-section" style={{ alignItems: "stretch", background: "#030303" }}>
        {/* Left text */}
        <div style={{
          flex: "0 0 50%", padding: "120px 80px",
          display: "flex", flexDirection: "column",
          justifyContent: "center", gap: 28,
          borderRight: `1px solid ${T.border}`,
        }}>
          <Reveal>
            <Tag>EXPLOITANTS ÉOLIENS & PV</Tag>
          </Reveal>
          <Reveal delay={0.06}>
            <SectionTitle>
              Dirigez vos<br />exploitations<br />avec précision.
            </SectionTitle>
          </Reveal>
          <Reveal delay={0.12}>
            <Body>
              Vous gérez des parcs éoliens ou photovoltaïques ? Nos abonnements vous
              donnent accès aux données brutes dont vous avez besoin : vitesse du vent
              u/v à 10m, rayonnement solaire direct, irradiance totale, température sol.
              <br /><br />
              <strong style={{ color: T.text }}>Économisez sur votre électricité</strong> en
              anticipant les pics de production et en optimisant vos décisions opérationnelles
              en temps réel.
            </Body>
          </Reveal>
          <Reveal delay={0.16}>
            {/* features list */}
            <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 4 }}>
              {[
                "Vitesse vent u/v à 10m",
                "Rayonnement solaire direct",
                "Irradiance Solaire Totale",
                "Température sol & humidité",
                "Export CSV / JSON / API",
              ].map(item => (
                <div key={item} style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <div style={{ width: 4, height: 4, borderRadius: "50%",
                    background: T.accent, flexShrink: 0 }} />
                  <span style={{ fontSize: 13, color: T.muted }}>{item}</span>
                </div>
              ))}
            </div>
          </Reveal>
          <Reveal delay={0.22}>
            <div style={{ display: "flex", gap: 16, marginTop: 8 }}>
              <button className="cta-btn cta-btn-solid" onClick={() => setActive("Pricing")}>
                VOIR LES ABONNEMENTS
              </button>
            </div>
          </Reveal>
        </div>

        {/* Right image */}
        <div style={{ flex: "0 0 50%", minHeight: "100vh" }}>
          <ImgPanel src={A.solar} position="center 50%" brightness={0.72}>
            <div style={{
              position: "absolute", bottom: 24, left: 24,
              fontSize: 9, color: "rgba(0,212,255,0.6)", letterSpacing: "1px",
            }}>
              PHOTOVOLTAÏQUE · FRANCE
            </div>
          </ImgPanel>
        </div>
      </section>

      {/* ════════════════════════════════════════════════════════════
          SECTION 4 — ACTUALITÉS
          Image gauche · Texte droite
      ════════════════════════════════════════════════════════════ */}
      <section className="home-section" style={{ alignItems: "stretch" }}>
        {/* Left image */}
        <div style={{ flex: "0 0 45%", minHeight: "100vh" }}>
          <ImgPanel src={A.lightning} position="center center" brightness={0.65}>
            <div style={{
              position: "absolute", bottom: 24, left: 24,
              fontSize: 9, color: "rgba(0,212,255,0.6)", letterSpacing: "1px",
            }}>
              PRÉVISION ORAGES · J+3
            </div>
          </ImgPanel>
        </div>

        {/* Right text */}
        <div style={{
          flex: "0 0 55%", padding: "120px 80px",
          display: "flex", flexDirection: "column",
          justifyContent: "center", gap: 28,
          borderLeft: `1px solid ${T.border}`,
        }}>
          <Reveal>
            <Tag>DERNIÈRES ACTUALITÉS</Tag>
          </Reveal>
          <Reveal delay={0.06}>
            <SectionTitle>
              Science, données<br />& météorologie<br />augmentée.
            </SectionTitle>
          </Reveal>

          {/* News items */}
          {[
            { date: "26/02/2026", tag: "LAUNCH", tagColor: T.accent,
              title: "Lancement ZOJZ Platform v1.0",
              desc: "La révolution de la prévision par IA GraphCast est désormais accessible aux exploitants français." },
            { date: "12/02/2026", tag: "TECH", tagColor: T.green,
              title: "Intégration Copernicus v5.1",
              desc: "Nouveau flux satellite haute résolution pour une analyse sol ultra-précise." },
            { date: "05/01/2026", tag: "ENERGY", tagColor: "#f97316",
              title: "Algorithmes Photovoltaïques v2",
              desc: "Calcul d'irradiance amélioré pour maximiser le rendement des parcs solaires." },
          ].map((n, i) => (
            <Reveal key={n.title} delay={0.10 + i * 0.06}>
              <div style={{
                paddingBottom: 24,
                borderBottom: `1px solid ${T.border}`,
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 8 }}>
                  <span style={{
                    fontSize: 8, fontWeight: 700, letterSpacing: "1.5px",
                    color: n.tagColor, border: `1px solid ${n.tagColor}55`,
                    padding: "2px 8px",
                  }}>{n.tag}</span>
                  <span style={{ fontSize: 10, color: T.muted }}>{n.date}</span>
                </div>
                <div style={{ fontSize: 15, color: "#fff", fontWeight: 700, marginBottom: 6 }}>{n.title}</div>
                <div style={{ fontSize: 13, color: T.muted, lineHeight: 1.65 }}>{n.desc}</div>
              </div>
            </Reveal>
          ))}

          <Reveal delay={0.32}>
            <button className="cta-btn" onClick={() => setActive("News")}>
              TOUTES LES ACTUALITÉS
              <ArrowUpRight size={14} />
            </button>
          </Reveal>
        </div>
      </section>

      {/* ════════════════════════════════════════════════════════════
          SECTION 5 — FOOTER CONTACT (style Atmo)
      ════════════════════════════════════════════════════════════ */}
      <footer className="footer-atmo" style={{
    background: T.yellow,
    color: "#111",
    fontFamily: "'JetBrains Mono', monospace",
    minHeight: "100vh",
    display: "flex",
    flexDirection: "column",
}}>
    {/* Main contact grid */}
    <div style={{
        flex: 1,
        display: "grid",
        gridTemplateColumns: "1fr 1fr 1fr",
        borderBottom: "1px solid rgba(0,0,0,0.15)",
    }}>
        {/* Col 1 — Adresse & Mission */}
        <div style={{
            padding: "60px 52px",
            borderRight: "1px solid rgba(0,0,0,0.15)",
            display: "flex", flexDirection: "column",
            justifyContent: "space-between",
        }}>
            <div>
                <div style={{ fontSize: 20, fontWeight: 700, letterSpacing: "4px", color: "#111", marginBottom: 20 }}>
                    Z O <span style={{ color: "#333" }}>J</span> Z
                </div>
                <div style={{ fontSize: 13, lineHeight: 1.5, maxWidth: 300, color: "rgba(0,0,0,0.7)" }}>
                    Intelligence météorologique appliquée aux énergies renouvelables. 
                    Calculs d'albédo et rugosité haute résolution pour parcs solaires et éoliens.
                </div>
            </div>
            <div style={{ fontSize: 11, color: "rgba(0,0,0,0.5)" }}>
                SIRET : EN COURS D'IMMATRICULATION
            </div>
        </div>

        {/* Col 2 — Inquiries */}
        <div style={{
            padding: "60px 52px",
            borderRight: "1px solid rgba(0,0,0,0.15)",
        }}>
            <div style={{ marginBottom: 32 }}>
                <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 6 }}>
                    General Inquiries
                </div>
                <a href="mailto:contact@zojz.fr" style={{ color: "#111", textDecoration: "none" }}>
                    contact@zojz.fr ↗
                </a>
            </div>
            <div style={{ marginBottom: 32 }}>
                <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 6 }}>
                    Data Source
                </div>
                <div style={{ fontSize: 13, color: "#111" }}>
                    Copernicus ERA5 / GraphCast v2<br />
                    Resolution: 0.25 km²
                </div>
            </div>
        </div>

        {/* Col 3 — Security & Trust */}
        <div style={{ padding: "60px 52px", display: "flex", flexDirection: "column", justifyContent: "flex-start" }}>
            <div style={{ marginBottom: 32 }}>
                <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 6 }}>
                    Secure Payments
                </div>
                <div style={{ fontSize: 13, color: "rgba(0,0,0,0.7)" }}>
                    Infrastucture de paiement certifiée via <strong>Stripe</strong>.<br />
                    Transactions sécurisées AES-256.
                </div>
            </div>
        </div>
    </div>

    {/* Footer bar */}
    <div style={{
        padding: "20px 52px",
        display: "flex", alignItems: "center",
        justifyContent: "space-between",
        borderTop: "1px solid rgba(0,0,0,0.15)",
    }}>
        <span style={{ fontSize: 13, color: "rgba(0,0,0,0.55)" }}>
            © 2026 ZOJZ · IA METEOROLOGY · FRANCE
        </span>
        <span style={{ fontSize: 13, color: "rgba(0,0,0,0.55)" }}>
            STATUS: OPERATIONAL
        </span>
    </div>
</footer>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// MAP SECTION — VERSION INDUSTRIELLE
// ─────────────────────────────────────────────────────────────────

function DataGridLayer({ points, onSelectPoint }) { // Ajout de la prop onSelectPoint
  if (!points || points.length === 0) return null;

  const getColor = (t) => {
    if (t > 35) return '#7f1d1d';
    if (t > 25) return '#ef4444';
    if (t > 15) return '#f59e0b';
    if (t > 5)  return '#10b981';
    if (t > 0)  return '#0ea5e9';
    return '#6366f1';
  };

  return (
    <>
      {points.map((p, idx) => {
        let tempC = parseFloat(p.value);
        if (tempC > 100) tempC = tempC - 273.15;
        if (tempC > 60 || tempC < -50) return null;

        const size = 0.125; 
        const bounds = [
          [p.latitude - size, p.longitude - size],
          [p.latitude + size, p.longitude + size]
        ];

        return (
          <Rectangle
            key={idx}
            bounds={bounds}
            eventHandlers={{
              click: () => onSelectPoint(p), // Déclenche la sélection au clic
            }}
            pathOptions={{
              stroke: true,
              weight: 0.5,
              color: '#ffffff22',
              fillColor: getColor(tempC),
              fillOpacity: 0.7
            }}
          >
            <Tooltip direction="top" offset={[0, -10]} opacity={1}>
              <div style={{ background: '#000', color: '#fff', padding: '5px', borderRadius: '4px' }}>
                <span style={{ fontWeight: 'bold' }}>{tempC.toFixed(1)}°C</span>
                <br/>
                <span style={{ fontSize: '10px', color: '#aaa' }}>{p.variable_name}</span>
              </div>
            </Tooltip>
          </Rectangle>
        );
      })}
    </>
  );
}

// MODIFICATION 1 : Ajout du style CSS pour le rectangle sélectionné
// (à placer juste avant la fonction MapSection)
const mapStyle = `
  .selected-rectangle {
    stroke: #00ffff !important;
    stroke-width: 3px !important;
    stroke-opacity: 1 !important;
    fill-color: #00ffff !important;
    fill-opacity: 0.5 !important;
  }
`;

function MapSection({ apiOnline, user, setActive }) {
  const [allData, setAllData] = useState([]); 
  const [assetType, setAssetType] = useState("wind"); 
  const [points, setPoints] = useState([]);   
  const [timeSteps, setTimeSteps] = useState([]); 
  const [currentTimeIdx, setCurrentTimeIdx] = useState(0);
  const [selectedPointCoords, setSelectedPointCoords] = useState(null); 
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({ tMax: null, tMin: null });
  const [expertAdvice, setExpertAdvice] = useState(null);
  const [loadingAdvice, setLoadingAdvice] = useState(false);
  const [isBifacial, setIsBifacial] = useState(false);
  const [roughnessType, setRoughnessType] = useState("open"); // open, forest, or urban

  // 1. CHARGEMENT INITIAL (CORRIGÉ : Uniquement les données météo)
  useEffect(() => {
    async function load() {
      setLoading(true);
      try {
        const res = await fetch('https://zojz.fr/api/forecast/predictions');
        const data = await res.json();
        if (!data || data.length === 0) return;
        setAllData(data);

        const now = new Date();
        now.setMinutes(0, 0, 0);
        const hourlySteps = [];
        for (let i = 0; i <= 24; i++) {
          const h = new Date(now);
          h.setHours(now.getHours() + i);
          const year = h.getFullYear();
          const month = String(h.getMonth() + 1).padStart(2, '0');
          const day = String(h.getDate()).padStart(2, '0');
          const hours = String(h.getHours()).padStart(2, '0');
          hourlySteps.push(`${year}-${month}-${day} ${hours}:00:00`);
        }
        setTimeSteps(hourlySteps);
        setCurrentTimeIdx(0);
      } catch (e) { console.error(e); } finally { setLoading(false); }
    }
    load();
  }, []);

  // 2. FILTRAGE PAR HEURE (Identique)
  useEffect(() => {
    if (allData.length === 0 || timeSteps.length === 0) return;
    const currentTimestamp = timeSteps[currentTimeIdx];
    const filteredForTime = allData.filter(d => {
      const cleanDB = d.datetime.replace('T', ' ').split('.')[0];
      const cleanSlider = currentTimestamp.replace('T', ' ').split('.')[0];
      return cleanDB.substring(0, 16) === cleanSlider.substring(0, 16);
    });

    const pointsMap = {};
    filteredForTime.forEach(p => {
      const lat = parseFloat(p.latitude).toFixed(2);
      const lon = parseFloat(p.longitude).toFixed(2);
      const key = `${lat}_${lon}`;
      if (!pointsMap[key]) pointsMap[key] = { latitude: parseFloat(lat), longitude: parseFloat(lon), temp: null, u: 0, v: 0 };
      const val = parseFloat(p.value);
      const name = p.variable_name.toLowerCase();
      if (name.includes('t2m') || name.includes('temp')) pointsMap[key].temp = val > 100 ? val - 273.15 : val;
      if (name.includes('u10')) pointsMap[key].u = val;
      if (name.includes('v10')) pointsMap[key].v = val;
    });

    const processed = Object.values(pointsMap).filter(p => p.temp !== null).map(p => {
      const speedKmh = Math.sqrt(Math.pow(p.u||0, 2) + Math.pow(p.v||0, 2)) * 3.6;
      let feels = p.temp;
      if (p.temp <= 10 && speedKmh > 4.8) {
        feels = 13.12 + 0.6215 * p.temp - 11.37 * Math.pow(speedKmh, 0.16) + 0.3965 * p.temp * Math.pow(speedKmh, 0.16);
      }
      return { ...p, value: p.temp, windSpeed: speedKmh, feelsLike: feels };
    });
    setPoints(processed);
    if (processed.length > 0) {
      const temps = processed.map(p => p.value);
      setStats({ tMax: Math.max(...temps).toFixed(1), tMin: Math.min(...temps).toFixed(1) });
    }
  }, [allData, timeSteps, currentTimeIdx]);

  // 3. RÉCUPÉRATION DU CONSEIL EXPERT (CORRIGÉ : Avec assetType)
  // 3. RÉCUPÉRATION DU CONSEIL EXPERT (CORRIGÉ : Avec Time Sync)
useEffect(() => {
    // On vérifie qu'on a un point, un utilisateur pro, et que la timeline est chargée
    if (selectedPointCoords && timeSteps.length > 0) {
        const getAdvice = async () => {
            setLoadingAdvice(true);
            try {
                // On récupère l'objet Date correspondant à l'index du curseur
                const timestamp = timeSteps[currentTimeIdx];
                
                const res = await fetch(
                    `https://zojz.fr/api/pro/advice?lat=${selectedPointCoords.lat}&lon=${selectedPointCoords.lon}&asset_type=${assetType}&selected_time=${timestamp}&is_bifacial=${isBifacial}&roughness=${roughnessType}`
                );
                const data = await res.json();
                setExpertAdvice(data);
            } catch (e) {
                setExpertAdvice({ action: "ERREUR", reason: "Serveur indisponible" });
            } finally {
                setLoadingAdvice(false);
            }
        };
        getAdvice();
    } else {
        setExpertAdvice(null);
    }
    // On ajoute timeSteps et currentTimeIdx pour que ça réagisse au mouvement du curseur
}, [selectedPointCoords, user, assetType, timeSteps, currentTimeIdx, isBifacial, roughnessType]); // <-- Ajout de assetType ici pour réagir au clic bouton

  const selectedPoint = selectedPointCoords 
    ? points.find(p => p.latitude === selectedPointCoords.lat && p.longitude === selectedPointCoords.lon)
    : null;

  const getColor = (t) => {
    if (t > 35) return '#7f1d1d'; if (t > 25) return '#ef4444'; if (t > 15) return '#f59e0b';
    if (t > 5)  return '#10b981'; if (t > 0)  return '#0ea5e9'; return '#6366f1';
  };

  return (
    <section style={{ height: "calc(100vh - 44px)", display: "grid", gridTemplateColumns: "repeat(24, 1fr)" }}>
      {/* PARTIE CARTE */}
      <div style={{ gridColumn: "1/19", position: "relative" }}>
        <div style={{ 
          position: "absolute", bottom: 30, left: "50%", transform: "translateX(-50%)",
          zIndex: 1000, width: "80%", background: "rgba(0,0,0,0.85)", padding: "15px 25px",
          borderRadius: "8px", border: "1px solid #1a1a1a", backdropFilter: "blur(10px)"
        }}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 10 }}>
            <span style={{ color: "#00f2ff", fontSize: 12, fontWeight: "bold" }}>PRÉVISION CHRONOLOGIQUE</span>
            <span style={{ color: "#fff", fontSize: 12 }}>{timeSteps[currentTimeIdx] ? new Date(timeSteps[currentTimeIdx]).toLocaleString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : "Chargement..."}</span>
          </div>
          <input type="range" min="0" max={timeSteps.length - 1} value={currentTimeIdx} onChange={(e) => setCurrentTimeIdx(parseInt(e.target.value))} style={{ width: "100%", cursor: "pointer", accentColor: "#00f2ff" }} />
        </div>

        <MapContainer center={[46.6, 2.2]} zoom={6} style={{ height: "100%", width: "100%", background: "#040d1a" }}>
          <TileLayer url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png" />
          {points.map((p, idx) => {
            const size = 0.125; 
            const bounds = [[p.latitude - size, p.longitude - size], [p.latitude + size, p.longitude + size]];
            const isSelected = selectedPointCoords && selectedPointCoords.lat === p.latitude && selectedPointCoords.lon === p.longitude;
            return (
              <Rectangle
                key={`${idx}-${currentTimeIdx}`}
                bounds={bounds}
                eventHandlers={{ click: () => setSelectedPointCoords({lat: p.latitude, lon: p.longitude}) }}
                pathOptions={{
                  stroke: isSelected, weight: isSelected ? 3 : 0.5,
                  color: isSelected ? '#00f2ff' : '#ffffff22',
                  fillColor: getColor(p.value), fillOpacity: 0.35
                }}
              />
            );
          })}
        </MapContainer>
        {loading && (
          <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.8)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1001 }}>
              <div style={{ color: "#00f2ff", fontWeight: "bold", textAlign: "center" }}>
                <p className="blink">INITIALISATION DES SÉRIES TEMPORELLES...</p>
              </div>
          </div>
        )}
      </div>
      
      {/* PANNEAU LATÉRAL ANALYSE */}
<div style={{ gridColumn: "19/25", background: "#050505", borderLeft: "1px solid #1a1a1a", padding: 30, display: "flex", flexDirection: "column", overflowY: 'auto' }}>
    <h2 style={{ fontSize: 11, color: "#00f2ff", letterSpacing: 2, marginBottom: 20 }}>ANALYSE LOCALE</h2>

    {/* SÉLECTEUR D'ASSET */}
<div style={{ display: "flex", gap: 8, marginBottom: 15 }}>
    {["wind", "solar"].map(type => (
        <button
            key={type}
            onClick={() => setAssetType(type)}
            style={{
                flex: 1, padding: "10px 5px", fontSize: 9, fontWeight: 800,
                background: assetType === type ? "rgba(0,242,255,0.08)" : "transparent",
                color: assetType === type ? "#00f2ff" : "#444",
                border: `1px solid ${assetType === type ? "#00f2ff" : "#222"}`,
                cursor: "pointer", transition: "all 0.2s", letterSpacing: 1,
                borderRadius: 2
            }}
        >
            {type === "wind" ? "🌬️ ÉOLIEN" : "☀️ SOLAIRE"}
        </button>
    ))}
</div>

{/* NOUVEAU : Toggle Bifacial pour le Solaire */}
{assetType === "solar" && (
    <button 
        onClick={() => setIsBifacial(!isBifacial)}
        style={{
            width: "100%", padding: "8px", fontSize: 8, fontWeight: 900,
            background: isBifacial ? "#00f2ff" : "transparent",
            color: isBifacial ? "#000" : "#666",
            border: `1px solid ${isBifacial ? "#00f2ff" : "#333"}`,
            borderRadius: 2, cursor: "pointer", marginBottom: 30,
            transition: "all 0.2s", letterSpacing: 1
        }}
    >
        MODE BIFACIAL : {isBifacial ? "ACTIVÉ (ALBÉDO)" : "DÉSACTIVÉ"}
    </button>
)}

{/* OPTIONS SPÉCIFIQUES ÉOLIEN (RUGOSITÉ) */}
{assetType === "wind" && (
    <div style={{ marginBottom: 30 }}>
        <p style={{ fontSize: 9, color: "#666", marginBottom: 10, letterSpacing: 1 }}>ENVIRONNEMENT DU SITE</p>
        <div style={{ display: "flex", gap: 4 }}>
            {[
                { id: "sea", label: "MER", icon: "🌊" },
                { id: "open", label: "PLAINE", icon: "🌾" },
                { id: "forest", label: "BOIS", icon: "🌳" },
                { id: "urban", label: "VILLE", icon: "🏙️" }
            ].map(t => (
                <button
                    key={t.id}
                    onClick={() => setRoughnessType(t.id)}
                    style={{
                        flex: 1, padding: "8px 2px", fontSize: 7, fontWeight: 800,
                        background: roughnessType === t.id ? "rgba(0,242,255,0.1)" : "transparent",
                        color: roughnessType === t.id ? "#00f2ff" : "#444",
                        border: `1px solid ${roughnessType === t.id ? "#00f2ff" : "#222"}`,
                        cursor: "pointer", borderRadius: 2, transition: "all 0.2s"
                    }}
                >
                    <div style={{ fontSize: 10, marginBottom: 2 }}>{t.icon}</div>
                    {t.label}
                </button>
            ))}
        </div>
    </div>
)}

    {selectedPoint ? (
        <div style={{ animation: "fadeIn 0.3s ease-in" }}>
            <p style={{ fontSize: 13, color: "#666", marginBottom: 5 }}>COORDONNÉES</p>
            <p style={{ fontSize: 14, color: "#fff", marginBottom: 20 }}>{selectedPoint.latitude.toFixed(3)}°N, {selectedPoint.longitude.toFixed(3)}°E</p>

            <p style={{ fontSize: 13, color: "#666", marginBottom: 10 }}>
                {assetType === "solar" ? "TEMPÉRATURE CELLULE" : "TEMPÉRATURE"}
            </p>
            <p style={{ fontSize: 56, color: "#ff8c00", fontWeight: 800, lineHeight: 1, marginBottom: 20 }}>
                {expertAdvice?.current_temp !== undefined ? expertAdvice.current_temp : selectedPoint.value.toFixed(1)}°C
            </p>

            <p style={{ fontSize: 11, color: "#666", marginBottom: 10 }}>RESSENTI ESTIMÉ</p>
            <p style={{ fontSize: 24, color: "#fff", fontWeight: 600, marginBottom: 20 }}>
              {/* CORRECTION : On utilise current_feels_like et non current_temp */}
              {expertAdvice?.current_feels_like !== undefined 
                ? expertAdvice.current_feels_like.toFixed(1) 
                : selectedPoint.value.toFixed(1)
              }°C
            </p>

            <p style={{ fontSize: 13, color: "#666", marginBottom: 10 }}>
                {assetType === "solar" ? "IRRADIANCE (ESTIMÉE)" : "VENT ESTIMÉ"}
            </p>
            <p style={{ fontSize: 24, color: "#fff", fontWeight: 600, marginBottom: 5 }}>
                {assetType === "solar" 
                  ? (expertAdvice?.current_irradiance !== undefined ? `${expertAdvice.current_irradiance} W/m²` : "CALCUL...") 
                  : (expertAdvice?.current_wind !== undefined ? `${expertAdvice.current_wind} km/h` : `${selectedPoint.windSpeed.toFixed(1)} km/h`)
                }
            </p>
            {assetType === "solar" && isBifacial && expertAdvice?.current_irradiance && (
                <p style={{ fontSize: 10, color: "#00f2ff", marginBottom: 25, fontWeight: 600 }}>
                    ✨ +{(expertAdvice.albedo_gain || 0).toFixed(0)} W/m² VIA RÉFLEXION SOLAIRE
                </p>
            )}

            {assetType === "wind" && expertAdvice?.wind_impact !== undefined && roughnessType !== "open" && (
              <p style={{ 
                 fontSize: 10, 
                  color: expertAdvice.wind_impact < 0 ? "#ff4444" : "#00ff88", 
                  marginBottom: 25, 
                  fontWeight: 600 
              }}>
                  {expertAdvice.wind_impact < 0 
                    ? `⚠ ${expertAdvice.wind_impact}% PERTE (RUGOSITÉ)` 
                    : `⚡ +${expertAdvice.wind_impact}% GAIN (FLUX MARIN)`}
              </p>
            )}

            {/* BLOC CONSEIL IA */}
            <div style={{ padding: 20, background: 'rgba(0,212,255,0.03)', border: `1px solid ${user?.plan_type === 'pro' ? '#00f2ff' : '#222'}`, position: 'relative', marginBottom: 20 }}>
                <h4 style={{ fontSize: 9, color: '#00f2ff', marginBottom: 15, fontWeight: 800, letterSpacing: 1 }}>
                    CONSEIL {assetType === "solar" ? "SOLAIRE" : "ÉOLIEN"}
                </h4>
                {user?.plan_type === 'pro' ? (
                <div>
                    {loadingAdvice ? ( <p style={{ fontSize: 12, color: '#555' }} className="blink">CALCUL IA EN COURS...</p> ) : (
                        <>
                            <p style={{ fontSize: 24, color: '#fff', fontWeight: 800, marginBottom: 5 }}> {expertAdvice?.action || "ANALYSE..."} </p>
                            <p style={{ fontSize: 11, color: '#aaa', lineHeight: 1.4 }}> {expertAdvice?.reason || "Récupération des données en cours."} </p>
                        </>
                    )}
                </div>
                ) : (
                <div style={{ position: 'relative' }}>
                    <div style={{ filter: 'blur(5px)', opacity: 0.3, userSelect: 'none' }}>
                        <p style={{ fontSize: 22, fontWeight: 800 }}>VENDRE</p>
                        <p style={{ fontSize: 11 }}>Conditions optimales détectées.</p>
                    </div>
                    <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center' }}>
                        <p style={{ fontSize: 10, fontWeight: 800, color: '#fff', marginBottom: 10 }}>RÉSERVÉ AUX COMPTES PRO</p>
                        <button
                          onClick={() => setActive("Pricing")}
                          style={{ background: '#00f2ff', color: '#000', border: 'none', padding: '6px 12px', fontSize: 9, fontWeight: 900, cursor: 'pointer' }}
                        > 
                          DÉBLOQUER L'ANALYSE
                        </button>
                    </div>
                </div>
                )}
            </div>

            {/* NOUVEAU : PANNEAU DE MÉTRIQUES PRO */}
            {user?.plan_type === 'pro' && expertAdvice?.pro_metrics && (
                <div style={{ padding: "15px 0", borderTop: "1px solid #1a1a1a" }}>
                    <h4 style={{ fontSize: 9, color: '#666', marginBottom: 15, fontWeight: 800, letterSpacing: 1 }}>MÉTRIQUES CERTIFIÉES</h4>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                        {Object.entries(expertAdvice.pro_metrics).map(([key, value]) => (
                            <div key={key}>
                                <p style={{ fontSize: 9, color: '#444', marginBottom: 4, textTransform: 'uppercase' }}>{key}</p>
                                <p style={{ fontSize: 14, color: '#fff', fontWeight: 600 }}>{value}</p>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    ) : (
        <p style={{ color: "#333", fontSize: 13, textAlign: "center", marginTop: 50 }}>SÉLECTIONNEZ UN POINT SUR LA TIMELINE</p>
    )}

    <div style={{ marginTop: 'auto', paddingTop: 20, borderTop: '1px solid #1a1a1a', fontSize: 11, color: '#444' }}>
        <p>VALEURS EXTRÊMES (FRANCE)</p>
        <p style={{ color: '#ef4444' }}>MAX : {stats.tMax}°C</p>
        <p style={{ color: '#0ea5e9' }}>MIN : {stats.tMin}°C</p>
    </div>
</div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────
// PRICING SECTION — INCHANGÉE (copie exacte v9)
// ─────────────────────────────────────────────────────────────────
function PricingSection() {
  const plans = [
    { 
      name: "EXPLORER", 
      price: "0€", 
      period: "/ mois",
      features: ["Carte temps réel France", "Vent (10m) & Température", "Résolution 0.25 km²", "Accès anonyme libre"],
      status: "current" // Statut spécial pour l'accès libre
    },
    { 
      name: "PROFESSIONNEL", 
      price: "14.99€", 
      period: "/ mois",
      features: ["Conseils IA (Vendre/Stocker)", "Mode Bifacial (Albédo)", "Rugosité du terrain (4 types)", "Vent Réel (100m)", "Métriques certifiées"],
      status: "popular"
    },
    { 
      name: "ENTREPRISE", 
      price: "CUSTOM", 
      period: "",
      features: ["Infrastructure Dédiée", "SLA 99.99%", "Export PDF & Rapports", "API haute performance", "Support Ingénieur 24/7"],
      status: "locked"
    },
  ];

  const T = {
    accent: "#00f2ff",
    border: "#1a1a1a",
    muted: "#666",
    bgCard: "rgba(255,255,255,0.01)",
    bgPopular: "rgba(0,242,255,0.02)"
  };

  return (
    <section style={{ padding: "100px 20px", background: "#000", fontFamily: "inherit" }}>
      <div style={{ textAlign: "center", marginBottom: 80 }}>
        <h2 style={{ fontSize: 48, color: "#fff", fontWeight: 800, margin: "0 0 15px 0", letterSpacing: "-1px" }}>
          ABONNEMENTS
        </h2>
        <p style={{ fontSize: 16, color: T.muted, maxWidth: 600, margin: "0 auto", lineHeight: 1.6 }}>
          De l'observation gratuite à l'expertise industrielle haute précision.
        </p>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 30, maxWidth: 1200, margin: "0 auto" }}>
        {plans.map((p) => {
          const isPopular = p.status === 'popular';
          const isLocked = p.status === 'locked';
          const isCurrent = p.status === 'current';

          return (
            <div key={p.name} style={{
              position: "relative",
              border: `1px solid ${isPopular ? T.accent : T.border}`,
              padding: "60px 40px",
              background: isPopular ? T.bgPopular : T.bgCard,
              display: "flex",
              flexDirection: "column",
              opacity: isLocked ? 0.5 : 1,
              transition: "all 0.3s ease"
            }}>
              {isPopular && (
                <div style={{ 
                  position: "absolute", top: -12, left: "50%", transform: "translateX(-50%)",
                  background: T.accent, color: "#000", padding: "4px 12px", fontSize: 10, fontWeight: 900, borderRadius: 2
                }}> RECOMMANDÉ </div>
              )}

              <span style={{ fontSize: 12, color: isPopular ? T.accent : "#444", fontWeight: 800, letterSpacing: 3, display: "block", marginBottom: 10 }}>
                {p.name}
              </span>

              <div style={{ display: "flex", alignItems: "baseline", gap: 5, margin: "20px 0" }}>
                <span style={{ fontSize: 54, fontWeight: 800, color: "#fff" }}>{p.price}</span>
                <span style={{ fontSize: 14, color: "#444" }}>{p.period}</span>
              </div>

              <div style={{ marginBottom: 50, flexGrow: 1 }}>
                {p.features.map(f => (
                  <div key={f} style={{ marginBottom: 15, display: "flex", alignItems: "flex-start", gap: 10 }}>
                    <span style={{ color: isLocked ? "#333" : T.accent, fontSize: 14 }}>✓</span>
                    <span style={{ fontSize: 13, color: "#aaa", lineHeight: 1.4 }}>{f}</span>
                  </div>
                ))}
              </div>

              <button 
                disabled={isLocked || isCurrent}
                onClick={(e) => {
                  e.preventDefault(); // Empêche le comportement par défaut de React
                  if (isPopular) {
                    // ⚠️ REMPLACE LE LIEN CI-DESSOUS PAR TON LIEN STRIPE RÉEL
                    const stripeUrl = "https://buy.stripe.com/test_bJeaEZd8EbCS54e5z3cQU00"; 
                    window.open(stripeUrl, "_blank"); // Ouvre Stripe dans un nouvel onglet
                  }
                }}
                style={{
                  width: "100%", padding: "18px", 
                  cursor: (isLocked || isCurrent) ? "default" : "pointer",
                  background: isPopular ? T.accent : "transparent",
                  border: `1px solid ${isPopular ? T.accent : (isCurrent ? "#222" : "#333")}`,
                  color: isPopular ? "#000" : (isCurrent ? T.muted : (isLocked ? "#333" : "#fff")),
                  fontWeight: 900, fontSize: 11, letterSpacing: 2,
                  textTransform: "uppercase",
                  transition: "all 0.2s"
                }}
              >
                {isCurrent ? "VOTRE ACCÈS ACTUEL" : 
                 isLocked ? "BIENTÔT DISPONIBLE" : 
                 "S'ABONNER MAINTENANT"}
              </button>
            </div>
          );
        })}
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────
// NEWS SECTION — INCHANGÉE (copie exacte v9)
// ─────────────────────────────────────────────────────────────────
function NewsSection() {
  // 1. ÉTAT POUR GÉRER L'AFFICHAGE (Liste ou Article seul)
  const [selectedArticle, setSelectedArticle] = React.useState(null);

  const news = [
    { 
      date: "21/05/2026", tag: "NEXT",
      title: "Et pour la suite ?",
      desc: "Quoi attendre pour la suite du projet ? Existe-t'il une RoadMap ?",
      content: `PROCHAINES ÉTAPES DU PROJET :
      
1. Amélioration de la couverture : passage d'un maillage lourd à un maillage découpé précisemment sur la France et ses alentours proches.

2. Nouvel Abonnement : Ajout de la fonctionalité secondaire du site, qui permettra à l'aide de l'abonnement PROFESSIONNEL d'avoir un accès
libre à toutes les données permettant de prendre les meilleures décisions concernant vos champs éoliens et photovolatïques.

3. Améliorations des prévisions : Nous proposerons régulièrement des mises à jour permettant une meilleure précision dans nos prévisions.

Restez donc branché pour la suite !`
    },
    { 
      date: "21/05/2026", tag: "TECH",
      title: "Comment ça marche ?",
      desc: "Explications de l'application ainsi que ses abonnements",
      content: `L'ARCHITECTURE TECHNIQUE :

La plateforme utilise GraphCast, un modèle de prévision météo basé sur des réseaux de neurones (GNN) développé par Google DeepMind. 
- Les données d'entrée proviennent du modèle ERA5.
- Le calcul est effectué sur des instances AWS équipées de GPU NVIDIA.
- Les résultats sont ensuite corrigés scientifiquement pour correspondre aux observations réelles locales.

Concernant les abonnements, une version Pro permettra bientôt d'accéder à des exports de données CSV et une résolution plus fine.`
    },
    { 
      date: "21/05/2026", tag: "LAUNCH",
      title: "Lancement de ZOJZ Platform v1.0",
      desc: "Lancement officiel de la plateforme !",
      content: `BIENVENUE SUR ZOJZ V1.0

Nous sommes fiers de vous présenter la première version de notre dashboard météo nouvelle génération. 

Nous avons choisi le nom "ZOJZ" car il est dans la mythologie albanaise archaïque le dieu du ciel, de la lumière, de la foudre et des tempêtes.
Il gouverne les phénomènes atmosphériques, et cela colle pour nous parfaitement avec notre service de prédiction météo.

Explorez la carte, utilisez le slider temporel et découvrez comment se comporte la météo dans votre zone !.`
    },
  ];

  // 2. AFFICHAGE DE L'ARTICLE SEUL
  if (selectedArticle) {
    return (
      <section style={{ padding: "80px 20px", maxWidth: 800, margin: "0 auto", minHeight: '80vh' }}>
        <button 
          onClick={() => setSelectedArticle(null)}
          style={{ 
            background: "none", border: "none", color: T.accent, 
            cursor: "pointer", marginBottom: 40, fontSize: 12, fontWeight: 700,
            padding: 0, letterSpacing: 1, textTransform: "uppercase"
          }}
        >
          ← Retour aux actualités
        </button>
        
        <div style={{ display: "flex", gap: 15, alignItems: "center", marginBottom: 20 }}>
          <span style={{
            fontSize: 9, fontWeight: 700, background: T.accent, color: "#000",
            padding: "2px 8px", borderRadius: 2,
          }}>{selectedArticle.tag}</span>
          <span style={{ fontSize: 12, color: T.muted }}>{selectedArticle.date}</span>
        </div>

        <h2 style={{ fontSize: 36, color: "#fff", fontWeight: 700, marginBottom: 30 }}>
          {selectedArticle.title}
        </h2>
        
        <div style={{ 
          color: "#ccc", lineHeight: 1.8, fontSize: 16, 
          whiteSpace: "pre-wrap", fontFamily: "inherit" 
        }}>
          {selectedArticle.content}
        </div>
      </section>
    );
  }

  // 3. AFFICHAGE DE LA LISTE (Vue par défaut)
  return (
    <section style={{ padding: 80, maxWidth: 900, margin: "0 auto" }}>
      <h2 style={{ fontSize: 36, color: "#fff", fontWeight: 700, marginBottom: 50, fontFamily: "inherit" }}>
        ACTUALITÉS &amp; RECHERCHE
      </h2>
      {news.map(n => (
        <div 
          key={n.title} 
          onClick={() => setSelectedArticle(n)}
          style={{ 
            padding: "40px 0", borderBottom: `1px solid ${T.border}`, 
            cursor: "pointer", transition: "all 0.2s ease" 
          }}
          className="news-item" // Tu peux ajouter un effet hover en CSS si tu veux
        >
          <div style={{ display: "flex", gap: 15, alignItems: "center", marginBottom: 12 }}>
            <span style={{
              fontSize: 9, fontWeight: 700, background: T.accent, color: "#000",
              padding: "2px 8px", borderRadius: 2,
            }}>{n.tag}</span>
            <span style={{ fontSize: 12, color: T.muted }}>{n.date}</span>
          </div>
          <h3 style={{ 
            fontSize: 22, color: "#fff", margin: "10px 0", 
            fontFamily: "inherit", transition: "color 0.2s" 
          }}>{n.title}</h3>
          <p style={{ color: T.muted, lineHeight: 1.7, fontSize: 14 }}>{n.desc}</p>
          <span style={{ color: T.accent, fontSize: 11, fontWeight: 700, marginTop: 15, display: 'block' }}>
            LIRE L'ARTICLE +
          </span>
        </div>
      ))}
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────
// COMPOSANT AUTHMODAL — Gestion Inscription / Connexion
// ─────────────────────────────────────────────────────────────────
function AuthModal({ isOpen, onClose, setUser }) {
  const [isLogin, setIsLogin] = React.useState(true);
  const [email, setEmail] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [error, setError] = React.useState("");

  if (!isOpen) return null;

  // AJOUT : Gestion du mot de passe oublié
  const handleForgotPassword = async () => {
    const mail = prompt("Entrez votre adresse email pour réinitialiser votre mot de passe :");
    if (!mail) return;
    try {
      await fetch("https://zojz.fr/api/pro/forgot-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: mail.toLowerCase().trim() })
      });
      alert("Si ce compte existe, un lien de récupération a été envoyé.");
    } catch (err) {
      alert("Erreur lors de la demande.");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    const endpoint = isLogin ? "login" : "register";
    
    try {
      const res = await fetch(`https://zojz.fr/api/pro/${endpoint}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.toLowerCase().trim(), password })
      });
      const data = await res.json();
      
      if (res.ok) {
        if (isLogin) {
          const userData = data.user;
          setUser(userData);
          localStorage.setItem("zojz_user", JSON.stringify(userData));
          onClose();
        } else {
          setIsLogin(true);
          alert("Compte créé avec succès ! Veuillez vous connecter.");
        }
      } else {
        setError(data.detail || "Erreur lors de l'authentification");
      }
    } catch (err) {
      setError("Le serveur PRO est injoignable.");
    }
  };

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 2000,
      background: "rgba(0,0,0,0.85)", backdropFilter: "blur(8px)",
      display: "flex", alignItems: "center", justifyContent: "center", padding: 20
    }}>
      <div style={{
        width: "100%", maxWidth: 360, background: "#050505",
        border: `1px solid ${T.border}`, padding: 40, position: "relative"
      }}>
        <button onClick={onClose} style={{ position: "absolute", top: 15, right: 15, background: "none", border: "none", color: "#444", cursor: "pointer", fontSize: 18 }}>✕</button>
        
        <h2 style={{ fontSize: 18, color: "#fff", marginBottom: 30, letterSpacing: 2, fontWeight: 700 }}>
          {isLogin ? "CONNEXION PRO" : "INSCRIPTION PRO"}
        </h2>
        
        <form onSubmit={handleSubmit}>
          <input type="email" placeholder="EMAIL" value={email} onChange={e => setEmail(e.target.value)} required
            style={{ width: "100%", background: "#111", border: "1px solid #222", color: "#fff", padding: "12px 15px", marginBottom: 15, fontSize: 12, outline: "none" }} />
          
          <input type="password" placeholder="MOT DE PASSE" value={password} onChange={e => setPassword(e.target.value)} required
            style={{ width: "100%", background: "#111", border: "1px solid #222", color: "#fff", padding: "12px 15px", marginBottom: 20, fontSize: 12, outline: "none" }} />
          
          {error && <div style={{ color: "#ef4444", fontSize: 10, marginBottom: 15, fontWeight: 700, textAlign: "center" }}>{error.toUpperCase()}</div>}
          
          <button type="submit" style={{ width: "100%", background: T.accent, color: "#000", border: "none", padding: 12, fontWeight: 900, fontSize: 11, cursor: "pointer", letterSpacing: 1 }}>
            {isLogin ? "SE CONNECTER" : "CRÉER MON COMPTE"}
          </button>
        </form>

        {/* AJOUT : Lien Mot de passe oublié */}
        {isLogin && (
          <p onClick={handleForgotPassword} style={{ color: T.muted, fontSize: 9, textAlign: "center", marginTop: 15, cursor: "pointer", opacity: 0.8 }}>
            MOT DE PASSE OUBLIÉ ?
          </p>
        )}

        <p onClick={() => setIsLogin(!isLogin)} style={{ color: T.muted, fontSize: 10, textAlign: "center", marginTop: 15, cursor: "pointer", textDecoration: "underline" }}>
          {isLogin ? "PAS ENCORE DE COMPTE ? S'INSCRIRE" : "DÉJÀ INSCRIT ? SE CONNECTER"}
        </p>
      </div>
    </div>
  );
}
// ─────────────────────────────────────────────────────────────────
// ROOT APP
// ─────────────────────────────────────────────────────────────────
export default function App() {
  const [active, setActive] = useState("Home");
  const apiOnline = useApiHealth();

  // 1. ÉTATS EXISTANTS
  const [showAuth, setShowAuth] = useState(false);
  const [user, setUser] = useState(() => {
    try {
      const saved = localStorage.getItem("zojz_user");
      // Sécurité anti-crash : on vérifie que c'est du JSON valide
      return (saved && saved !== "undefined") ? JSON.parse(saved) : null;
    } catch (e) { return null; }
  });

  const [showSuccessBanner, setShowSuccessBanner] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const [resetEmail, setResetEmail] = useState("");

  // DÉTECTION DU RESET (Ouverture de la modale automatique)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    
    // Si Stripe dit success
    if (params.get('success') === 'true') {
      setShowSuccessBanner(true);
    }

    // Si le mail de Reset est cliqué
    if (params.has("email")) {
      setResetEmail(params.get("email"));
      setShowResetModal(true); // On ouvre TA modale au lieu d'un prompt
    }
  }, []);

  return (
    <div style={{ background: "#000", minHeight: "100vh", position: "relative" }}>
      <style dangerouslySetInnerHTML={{ __html: CSS }} />

      {/* 1. BANNIÈRE STRIPE */}
      {showSuccessBanner && (
        <motion.div 
          initial={{ y: -100, x: '-50%', opacity: 0 }}
          animate={{ y: 20, x: '-50%', opacity: 1 }}
          style={{
            position: 'fixed', top: 0, left: '50%',
            backgroundColor: '#00f2ff', color: '#000', padding: '15px 30px',
            borderRadius: '4px', zIndex: 10000, fontWeight: 800,
            fontFamily: "'JetBrains Mono', monospace", border: '2px solid #fff'
          }}
        >
          🚀 PAIEMENT RÉUSSI ! VOTRE EXPERTISE PRO EST ACTIVÉE.
          <button onClick={() => setShowSuccessBanner(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', marginLeft: '10px' }}>✕</button>
        </motion.div>
      )}
      
      {/* 2. NAVBAR */}
      <Navbar 
        active={active} 
        setActive={setActive} 
        apiOnline={apiOnline} 
        user={user}
        setUser={setUser}
        setShowAuth={setShowAuth}
      />

      <div style={{ paddingTop: 44 }}>
        <AnimatePresence mode="wait">
          <motion.div
            key={active}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3 }}
          >
            {active === "Home"    && <HomeSection setActive={setActive} />}
            {active === "Map" && <MapSection apiOnline={apiOnline} user={user} setActive={setActive} />}
            {active === "Pricing" && <PricingSection />}
            {active === "News"    && <NewsSection />}
          </motion.div>
        </AnimatePresence>
      </div>

      {/* 3. MODALE CONNEXION */}
      <AuthModal 
        isOpen={showAuth} 
        onClose={() => setShowAuth(false)} 
        setUser={setUser} 
      />

      {/* 4. MODALE RESET (PLACÉE CORRECTEMENT DANS LE RETURN) */}
      {showResetModal && (
        <div style={{
          position: "fixed", inset: 0, zIndex: 3000,
          background: "rgba(0,0,0,0.9)", backdropFilter: "blur(10px)",
          display: "flex", alignItems: "center", justifyContent: "center", padding: 20
        }}>
          <div style={{ 
            background: "#050505", border: "1px solid #00e5ff", 
            padding: 40, width: "100%", maxWidth: 320, textAlign: "center" 
          }}>
            <h3 style={{ color: "#fff", fontSize: 14, marginBottom: 25, letterSpacing: 2 }}>
              NOUVEAU MOT DE PASSE
            </h3>
            
            <input 
              type="password" 
              id="new-password-input"
              placeholder="ENTREZ LE NOUVEAU MDP"
              style={{ 
                width: "100%", padding: 12, background: "#111", 
                color: "#fff", border: "1px solid #222", marginBottom: 20,
                fontSize: 12, outline: "none"
              }}
            />
            
            <button 
              onClick={async () => {
                const newPass = document.getElementById('new-password-input').value;
                if (!newPass) return alert("Veuillez saisir un mot de passe.");
                try {
                  const res = await fetch("https://zojz.fr/api/pro/update-password", {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ email: resetEmail, password: newPass })
                  });
                  if (res.ok) {
                    alert("✅ MOT DE PASSE MIS À JOUR !");
                    setShowResetModal(false);
                    window.history.replaceState({}, document.title, "/"); // Nettoie l'URL
                  } else {
                    alert("Erreur lors de la mise à jour.");
                  }
                } catch (err) { alert("Serveur injoignable."); }
              }}
              style={{ 
                width: "100%", background: "#00e5ff", color: "#000", 
                border: "none", padding: 12, fontWeight: 900, 
                fontSize: 11, cursor: "pointer", letterSpacing: 1 
              }}
            >
              VALIDER LE CHANGEMENT
            </button>
            
            <p onClick={() => setShowResetModal(false)} style={{ color: "#444", fontSize: 10, marginTop: 20, cursor: "pointer", textDecoration: "underline" }}>
              ANNULER
            </p>
          </div>
        </div>
      )}
    </div>
  );
}